import type {
  TrendTopic,
  ContentAsset,
  AgentStatus,
  PipelineWeek,
  PlatformDistribution,
} from '../lib/index';

// Mock Trend Topics - 5 UX Design Trends
export const mockTrends: TrendTopic[] = [
  {
    id: 'trend-001',
    name: 'AI Micro-interactions',
    category: 'Interaction Design',
    description: 'Subtle AI-powered animations and feedback mechanisms that enhance user experience through predictive and adaptive micro-interactions.',
    popularity: 92,
    growthRate: 34.5,
    keywords: [
      'AI interactions',
      'micro-animations',
      'predictive UX',
      'adaptive design',
      'smart feedback',
      'machine learning UI',
    ],
    sourceUrls: [
      'https://dribbble.com/tags/ai-interactions',
      'https://www.awwwards.com/ai-micro-interactions',
      'https://uxdesign.cc/ai-microinteractions-2024',
    ],
    discoveredAt: new Date('2024-01-15T10:30:00Z'),
    lastUpdated: new Date('2024-01-20T14:22:00Z'),
    status: 'trending',
    sentiment: 'positive',
    relevanceScore: 95,
  },
  {
    id: 'trend-002',
    name: 'Voice UI Evolution',
    category: 'Voice Design',
    description: 'Next-generation voice user interfaces with natural language processing, emotional intelligence, and multimodal interactions.',
    popularity: 87,
    growthRate: 28.3,
    keywords: [
      'voice interface',
      'conversational UI',
      'NLP design',
      'voice commands',
      'audio UX',
      'multimodal interaction',
    ],
    sourceUrls: [
      'https://voiceui.design/trends',
      'https://www.nngroup.com/articles/voice-ui-2024',
      'https://medium.com/voice-tech-podcast',
    ],
    discoveredAt: new Date('2024-01-12T08:15:00Z'),
    lastUpdated: new Date('2024-01-20T09:45:00Z'),
    status: 'trending',
    sentiment: 'positive',
    relevanceScore: 89,
  },
  {
    id: 'trend-003',
    name: 'Ethical Dark Patterns',
    category: 'UX Ethics',
    description: 'Growing awareness and regulation around deceptive design patterns, with focus on transparent and user-respecting interfaces.',
    popularity: 78,
    growthRate: 45.7,
    keywords: [
      'dark patterns',
      'ethical design',
      'user consent',
      'transparent UX',
      'privacy design',
      'deceptive patterns',
    ],
    sourceUrls: [
      'https://www.deceptive.design',
      'https://darkpatterns.org',
      'https://uxdesign.cc/ethical-design-2024',
    ],
    discoveredAt: new Date('2024-01-10T12:00:00Z'),
    lastUpdated: new Date('2024-01-19T16:30:00Z'),
    status: 'emerging',
    sentiment: 'positive',
    relevanceScore: 82,
  },
  {
    id: 'trend-004',
    name: 'Spatial Computing UX',
    category: 'Immersive Design',
    description: 'Design principles for AR/VR and mixed reality experiences, focusing on 3D spatial interfaces and gesture-based interactions.',
    popularity: 85,
    growthRate: 52.1,
    keywords: [
      'spatial design',
      'AR/VR UX',
      'mixed reality',
      '3D interfaces',
      'gesture control',
      'immersive experience',
    ],
    sourceUrls: [
      'https://www.spatialcomputing.design',
      'https://developer.apple.com/visionos/design',
      'https://www.meta.com/quest/design',
    ],
    discoveredAt: new Date('2024-01-08T14:20:00Z'),
    lastUpdated: new Date('2024-01-20T11:15:00Z'),
    status: 'trending',
    sentiment: 'positive',
    relevanceScore: 91,
  },
  {
    id: 'trend-005',
    name: 'Accessibility-First Design',
    category: 'Inclusive Design',
    description: 'Shift from accessibility as afterthought to core design principle, with WCAG 3.0 compliance and inclusive design systems.',
    popularity: 94,
    growthRate: 38.9,
    keywords: [
      'accessibility',
      'inclusive design',
      'WCAG compliance',
      'universal design',
      'assistive technology',
      'a11y',
    ],
    sourceUrls: [
      'https://www.a11yproject.com',
      'https://www.w3.org/WAI/WCAG3',
      'https://inclusive-components.design',
    ],
    discoveredAt: new Date('2024-01-05T09:00:00Z'),
    lastUpdated: new Date('2024-01-20T13:50:00Z'),
    status: 'stable',
    sentiment: 'positive',
    relevanceScore: 97,
  },
];

// Mock Agent Tasks
export const mockAgentTasks: AgentStatus[] = [
  {
    id: 'agent-001',
    name: 'TrendScout Alpha',
    type: 'trend-scout',
    status: 'active',
    currentTask: 'Scanning design communities for emerging patterns',
    tasksCompleted: 1247,
    tasksInQueue: 3,
    lastActiveAt: new Date('2024-01-20T14:55:00Z'),
    uptime: 2592000, // 30 days
    performance: {
      successRate: 96.8,
      averageTaskTime: 180,
      errorCount: 12,
    },
    capabilities: [
      'Web scraping',
      'Social media monitoring',
      'Trend analysis',
      'Keyword extraction',
      'Sentiment analysis',
    ],
    configuration: {
      scanInterval: 3600,
      sources: ['dribbble', 'behance', 'awwwards', 'twitter', 'reddit'],
      minRelevanceScore: 70,
    },
  },
  {
    id: 'agent-002',
    name: 'ContentGen Pro',
    type: 'content-generator',
    status: 'busy',
    currentTask: 'Generating blog post: "5 Ways AI Micro-interactions Transform UX"',
    tasksCompleted: 892,
    tasksInQueue: 8,
    lastActiveAt: new Date('2024-01-20T14:58:00Z'),
    uptime: 2505600, // 29 days
    performance: {
      successRate: 94.2,
      averageTaskTime: 420,
      errorCount: 23,
    },
    capabilities: [
      'Article writing',
      'Social media posts',
      'Video scripts',
      'Infographic content',
      'SEO optimization',
    ],
    configuration: {
      model: 'gpt-4-turbo',
      temperature: 0.7,
      maxTokens: 2000,
      tone: 'professional-friendly',
    },
  },
  {
    id: 'agent-003',
    name: 'VisualForge',
    type: 'content-generator',
    status: 'active',
    currentTask: 'Creating infographic: "Spatial Computing UX Best Practices"',
    tasksCompleted: 634,
    tasksInQueue: 5,
    lastActiveAt: new Date('2024-01-20T14:52:00Z'),
    uptime: 2419200, // 28 days
    performance: {
      successRate: 91.5,
      averageTaskTime: 600,
      errorCount: 31,
    },
    capabilities: [
      'Infographic generation',
      'Social media graphics',
      'Thumbnail creation',
      'Brand consistency',
      'Template customization',
    ],
    configuration: {
      imageModel: 'dall-e-3',
      resolution: '1024x1024',
      style: 'modern-minimal',
      brandColors: ['#6366F1', '#8B5CF6', '#EC4899'],
    },
  },
  {
    id: 'agent-004',
    name: 'VideoReel AI',
    type: 'content-generator',
    status: 'idle',
    currentTask: undefined,
    tasksCompleted: 456,
    tasksInQueue: 2,
    lastActiveAt: new Date('2024-01-20T14:30:00Z'),
    uptime: 2332800, // 27 days
    performance: {
      successRate: 88.7,
      averageTaskTime: 900,
      errorCount: 45,
    },
    capabilities: [
      'Short-form video creation',
      'Reels & TikToks',
      'YouTube Shorts',
      'Auto-captioning',
      'Trend-based editing',
    ],
    configuration: {
      videoLength: '15-60s',
      format: 'vertical-9:16',
      captionStyle: 'dynamic',
      musicLibrary: 'royalty-free',
    },
  },
  {
    id: 'agent-005',
    name: 'OptiMax',
    type: 'optimizer',
    status: 'active',
    currentTask: 'Optimizing content for Instagram engagement',
    tasksCompleted: 2103,
    tasksInQueue: 12,
    lastActiveAt: new Date('2024-01-20T14:57:00Z'),
    uptime: 2678400, // 31 days
    performance: {
      successRate: 97.3,
      averageTaskTime: 90,
      errorCount: 8,
    },
    capabilities: [
      'SEO optimization',
      'Hashtag generation',
      'A/B testing',
      'Performance prediction',
      'Platform-specific formatting',
    ],
    configuration: {
      platforms: ['instagram', 'tiktok', 'youtube', 'linkedin'],
      optimizationGoal: 'engagement',
      testingEnabled: true,
    },
  },
  {
    id: 'agent-006',
    name: 'ScheduleMaster',
    type: 'scheduler',
    status: 'active',
    currentTask: 'Analyzing optimal posting times for next week',
    tasksCompleted: 1876,
    tasksInQueue: 6,
    lastActiveAt: new Date('2024-01-20T14:56:00Z'),
    uptime: 2592000, // 30 days
    performance: {
      successRate: 99.1,
      averageTaskTime: 45,
      errorCount: 3,
    },
    capabilities: [
      'Optimal time prediction',
      'Content calendar management',
      'Cross-platform scheduling',
      'Audience timezone analysis',
      'Conflict resolution',
    ],
    configuration: {
      timezones: ['PST', 'EST', 'GMT', 'IST'],
      bufferTime: 300,
      maxDailyPosts: 5,
    },
  },
  {
    id: 'agent-007',
    name: 'AnalyticsBot',
    type: 'analyzer',
    status: 'busy',
    currentTask: 'Generating weekly performance report',
    tasksCompleted: 1543,
    tasksInQueue: 4,
    lastActiveAt: new Date('2024-01-20T14:59:00Z'),
    uptime: 2505600, // 29 days
    performance: {
      successRate: 98.5,
      averageTaskTime: 120,
      errorCount: 5,
    },
    capabilities: [
      'Performance tracking',
      'Engagement analysis',
      'Trend correlation',
      'ROI calculation',
      'Predictive analytics',
    ],
    configuration: {
      reportingFrequency: 'daily',
      metricsTracked: ['views', 'engagement', 'reach', 'conversions'],
      alertThresholds: { engagement: 5.0, views: 10000 },
    },
  },
];

// Mock Content Assets
export const mockContentAssets: ContentAsset[] = [
  {
    id: 'content-001',
    trendTopicId: 'trend-001',
    title: '5 AI Micro-interactions That Will Transform Your UX in 2024',
    description: 'Explore how AI-powered micro-interactions are revolutionizing user experience design with predictive animations and smart feedback.',
    contentType: 'article',
    format: 'text',
    platform: 'blog',
    status: 'approved',
    createdAt: new Date('2024-01-18T10:00:00Z'),
    updatedAt: new Date('2024-01-19T14:30:00Z'),
    scheduledFor: new Date('2024-01-22T09:00:00Z'),
    generatedBy: 'agent-002',
    wordCount: 1850,
    thumbnailUrl: '/thumbnails/ai-microinteractions-blog.jpg',
    contentUrl: '/content/ai-microinteractions-article.md',
    tags: ['AI', 'UX Design', 'Micro-interactions', 'Trends 2024', 'User Experience'],
    performanceScore: 88,
  },
  {
    id: 'content-002',
    trendTopicId: 'trend-001',
    title: 'AI Micro-interactions Explained',
    description: 'Visual breakdown of AI-powered micro-interactions and their impact on modern UX design.',
    contentType: 'infographic',
    format: 'image',
    platform: 'instagram',
    status: 'pending-review',
    createdAt: new Date('2024-01-19T11:30:00Z'),
    updatedAt: new Date('2024-01-19T15:45:00Z'),
    generatedBy: 'agent-003',
    dimensions: {
      width: 1080,
      height: 1350,
    },
    thumbnailUrl: '/thumbnails/ai-microinteractions-infographic.jpg',
    contentUrl: '/content/ai-microinteractions-infographic.png',
    tags: ['Infographic', 'AI', 'UX', 'Design Education'],
    performanceScore: 0,
  },
  {
    id: 'content-003',
    trendTopicId: 'trend-001',
    title: 'Watch AI Transform UX ✨',
    description: '60-second reel showcasing real examples of AI micro-interactions in popular apps.',
    contentType: 'video',
    format: 'video',
    platform: 'instagram',
    status: 'scheduled',
    createdAt: new Date('2024-01-19T14:00:00Z'),
    updatedAt: new Date('2024-01-20T09:00:00Z'),
    scheduledFor: new Date('2024-01-21T17:00:00Z'),
    generatedBy: 'agent-004',
    duration: 58,
    dimensions: {
      width: 1080,
      height: 1920,
    },
    thumbnailUrl: '/thumbnails/ai-microinteractions-reel.jpg',
    contentUrl: '/content/ai-microinteractions-reel.mp4',
    tags: ['Reels', 'AI', 'UX Design', 'Tutorial', 'Short Form'],
    performanceScore: 0,
  },
  {
    id: 'content-004',
    trendTopicId: 'trend-002',
    title: 'The Future of Voice UI: Beyond "Hey Siri"',
    description: 'Deep dive into next-generation voice interfaces with emotional intelligence and natural conversations.',
    contentType: 'article',
    format: 'text',
    platform: 'linkedin',
    status: 'published',
    createdAt: new Date('2024-01-15T08:00:00Z'),
    updatedAt: new Date('2024-01-16T10:30:00Z'),
    publishedAt: new Date('2024-01-17T12:00:00Z'),
    generatedBy: 'agent-002',
    wordCount: 2100,
    thumbnailUrl: '/thumbnails/voice-ui-article.jpg',
    contentUrl: '/content/voice-ui-future.md',
    tags: ['Voice UI', 'Conversational Design', 'NLP', 'Future Tech'],
    engagementMetrics: {
      views: 8542,
      likes: 423,
      shares: 87,
      comments: 56,
      saves: 234,
    },
    performanceScore: 92,
  },
  {
    id: 'content-005',
    trendTopicId: 'trend-003',
    title: 'Dark Patterns Exposed: What Designers Need to Know',
    description: 'Comprehensive guide to identifying and avoiding deceptive design patterns in 2024.',
    contentType: 'article',
    format: 'text',
    platform: 'blog',
    status: 'draft',
    createdAt: new Date('2024-01-20T09:00:00Z'),
    updatedAt: new Date('2024-01-20T11:30:00Z'),
    generatedBy: 'agent-002',
    wordCount: 1650,
    thumbnailUrl: '/thumbnails/dark-patterns-blog.jpg',
    contentUrl: '/content/dark-patterns-guide.md',
    tags: ['Ethics', 'Dark Patterns', 'UX Design', 'Best Practices'],
    performanceScore: 0,
  },
  {
    id: 'content-006',
    trendTopicId: 'trend-004',
    title: 'Spatial Computing UX Principles',
    description: 'Essential design principles for creating immersive AR/VR experiences.',
    contentType: 'infographic',
    format: 'image',
    platform: 'pinterest',
    status: 'approved',
    createdAt: new Date('2024-01-18T13:00:00Z'),
    updatedAt: new Date('2024-01-19T16:00:00Z'),
    scheduledFor: new Date('2024-01-23T10:00:00Z'),
    generatedBy: 'agent-003',
    dimensions: {
      width: 1000,
      height: 1500,
    },
    thumbnailUrl: '/thumbnails/spatial-computing-infographic.jpg',
    contentUrl: '/content/spatial-computing-principles.png',
    tags: ['Spatial Computing', 'AR/VR', 'Design Principles', 'Infographic'],
    performanceScore: 85,
  },
  {
    id: 'content-007',
    trendTopicId: 'trend-005',
    title: 'Accessibility-First Design in 60 Seconds',
    description: 'Quick guide to implementing accessibility from day one of your design process.',
    contentType: 'video',
    format: 'video',
    platform: 'youtube',
    status: 'published',
    createdAt: new Date('2024-01-14T10:00:00Z'),
    updatedAt: new Date('2024-01-15T14:00:00Z'),
    publishedAt: new Date('2024-01-16T15:00:00Z'),
    generatedBy: 'agent-004',
    duration: 60,
    dimensions: {
      width: 1080,
      height: 1920,
    },
    thumbnailUrl: '/thumbnails/accessibility-short.jpg',
    contentUrl: '/content/accessibility-first-short.mp4',
    tags: ['Accessibility', 'A11y', 'Inclusive Design', 'YouTube Shorts'],
    engagementMetrics: {
      views: 15234,
      likes: 892,
      shares: 156,
      comments: 78,
      saves: 445,
    },
    performanceScore: 94,
  },
  {
    id: 'content-008',
    trendTopicId: 'trend-005',
    title: 'WCAG 3.0: What Changed and Why It Matters',
    description: 'Comprehensive breakdown of WCAG 3.0 updates and their impact on design workflows.',
    contentType: 'article',
    format: 'text',
    platform: 'linkedin',
    status: 'scheduled',
    createdAt: new Date('2024-01-19T09:00:00Z'),
    updatedAt: new Date('2024-01-20T10:00:00Z'),
    scheduledFor: new Date('2024-01-24T11:00:00Z'),
    generatedBy: 'agent-002',
    wordCount: 1920,
    thumbnailUrl: '/thumbnails/wcag-3-article.jpg',
    contentUrl: '/content/wcag-3-guide.md',
    tags: ['WCAG', 'Accessibility', 'Standards', 'Compliance'],
    performanceScore: 0,
  },
];

// Mock Pipeline Weeks (4 weeks history)
export const mockPipelineWeeks: PipelineWeek[] = [
  {
    id: 'week-001',
    weekNumber: 3,
    year: 2024,
    startDate: new Date('2024-01-15T00:00:00Z'),
    endDate: new Date('2024-01-21T23:59:59Z'),
    status: 'in-progress',
    trends: [
      {
        trendTopicId: 'trend-001',
        priority: 'high',
        targetContentCount: 5,
        completedContentCount: 3,
      },
      {
        trendTopicId: 'trend-002',
        priority: 'medium',
        targetContentCount: 3,
        completedContentCount: 2,
      },
      {
        trendTopicId: 'trend-005',
        priority: 'high',
        targetContentCount: 4,
        completedContentCount: 3,
      },
    ],
    contentAssets: ['content-001', 'content-002', 'content-003', 'content-004', 'content-006', 'content-008'],
    milestones: [
      {
        id: 'milestone-001',
        name: 'AI Micro-interactions content suite complete',
        dueDate: new Date('2024-01-19T17:00:00Z'),
        completed: false,
      },
      {
        id: 'milestone-002',
        name: 'Voice UI article published',
        dueDate: new Date('2024-01-17T12:00:00Z'),
        completed: true,
        completedAt: new Date('2024-01-17T12:05:00Z'),
      },
      {
        id: 'milestone-003',
        name: 'Accessibility content scheduled',
        dueDate: new Date('2024-01-20T16:00:00Z'),
        completed: true,
        completedAt: new Date('2024-01-20T10:30:00Z'),
      },
    ],
    teamMembers: ['user-001', 'user-002', 'user-003'],
    notes: 'Focus on AI and accessibility trends this week. High engagement expected.',
  },
  {
    id: 'week-002',
    weekNumber: 2,
    year: 2024,
    startDate: new Date('2024-01-08T00:00:00Z'),
    endDate: new Date('2024-01-14T23:59:59Z'),
    status: 'completed',
    trends: [
      {
        trendTopicId: 'trend-003',
        priority: 'high',
        targetContentCount: 4,
        completedContentCount: 4,
      },
      {
        trendTopicId: 'trend-004',
        priority: 'medium',
        targetContentCount: 3,
        completedContentCount: 3,
      },
    ],
    contentAssets: ['content-005', 'content-007'],
    milestones: [
      {
        id: 'milestone-004',
        name: 'Dark patterns article series',
        dueDate: new Date('2024-01-12T17:00:00Z'),
        completed: true,
        completedAt: new Date('2024-01-12T15:30:00Z'),
      },
      {
        id: 'milestone-005',
        name: 'Spatial computing infographic',
        dueDate: new Date('2024-01-14T17:00:00Z'),
        completed: true,
        completedAt: new Date('2024-01-14T16:45:00Z'),
      },
    ],
    teamMembers: ['user-001', 'user-003', 'user-004'],
    notes: 'Successfully completed all ethical design content. Great team performance.',
  },
  {
    id: 'week-003',
    weekNumber: 1,
    year: 2024,
    startDate: new Date('2024-01-01T00:00:00Z'),
    endDate: new Date('2024-01-07T23:59:59Z'),
    status: 'completed',
    trends: [
      {
        trendTopicId: 'trend-001',
        priority: 'medium',
        targetContentCount: 3,
        completedContentCount: 3,
      },
      {
        trendTopicId: 'trend-005',
        priority: 'high',
        targetContentCount: 5,
        completedContentCount: 5,
      },
    ],
    contentAssets: [],
    milestones: [
      {
        id: 'milestone-006',
        name: 'New year content kickoff',
        dueDate: new Date('2024-01-03T12:00:00Z'),
        completed: true,
        completedAt: new Date('2024-01-03T11:30:00Z'),
      },
      {
        id: 'milestone-007',
        name: 'Accessibility week launch',
        dueDate: new Date('2024-01-07T17:00:00Z'),
        completed: true,
        completedAt: new Date('2024-01-07T16:00:00Z'),
      },
    ],
    teamMembers: ['user-001', 'user-002'],
    notes: 'Strong start to 2024. Accessibility content performed exceptionally well.',
  },
  {
    id: 'week-004',
    weekNumber: 52,
    year: 2023,
    startDate: new Date('2023-12-25T00:00:00Z'),
    endDate: new Date('2023-12-31T23:59:59Z'),
    status: 'completed',
    trends: [
      {
        trendTopicId: 'trend-002',
        priority: 'low',
        targetContentCount: 2,
        completedContentCount: 2,
      },
      {
        trendTopicId: 'trend-004',
        priority: 'medium',
        targetContentCount: 3,
        completedContentCount: 3,
      },
    ],
    contentAssets: [],
    milestones: [
      {
        id: 'milestone-008',
        name: 'Year-end review content',
        dueDate: new Date('2023-12-30T17:00:00Z'),
        completed: true,
        completedAt: new Date('2023-12-30T14:20:00Z'),
      },
    ],
    teamMembers: ['user-001', 'user-003'],
    notes: 'Holiday week with reduced output. Focus on planning 2024 strategy.',
  },
];

// Mock Distribution Platforms
export const mockDistributionPlatforms: PlatformDistribution[] = [
  {
    platform: 'youtube',
    contentCount: 24,
    scheduledCount: 3,
    publishedCount: 21,
    engagementRate: 8.7,
    reachEstimate: 125000,
    priority: 'high',
    bestPostingTimes: ['2024-01-21T15:00:00Z', '2024-01-21T18:00:00Z', '2024-01-22T12:00:00Z'],
    audienceSize: 45200,
    growthRate: 12.3,
  },
  {
    platform: 'instagram',
    contentCount: 38,
    scheduledCount: 5,
    publishedCount: 33,
    engagementRate: 6.4,
    reachEstimate: 98000,
    priority: 'high',
    bestPostingTimes: ['2024-01-21T09:00:00Z', '2024-01-21T17:00:00Z', '2024-01-21T20:00:00Z'],
    audienceSize: 38900,
    growthRate: 15.7,
  },
  {
    platform: 'linkedin',
    contentCount: 28,
    scheduledCount: 4,
    publishedCount: 24,
    engagementRate: 5.2,
    reachEstimate: 67000,
    priority: 'high',
    bestPostingTimes: ['2024-01-22T08:00:00Z', '2024-01-22T11:00:00Z', '2024-01-23T14:00:00Z'],
    audienceSize: 28500,
    growthRate: 9.8,
  },
  {
    platform: 'tiktok',
    contentCount: 19,
    scheduledCount: 2,
    publishedCount: 17,
    engagementRate: 11.3,
    reachEstimate: 156000,
    priority: 'medium',
    bestPostingTimes: ['2024-01-21T16:00:00Z', '2024-01-21T19:00:00Z', '2024-01-22T21:00:00Z'],
    audienceSize: 22100,
    growthRate: 28.4,
  },
  {
    platform: 'pinterest',
    contentCount: 31,
    scheduledCount: 6,
    publishedCount: 25,
    engagementRate: 4.8,
    reachEstimate: 52000,
    priority: 'medium',
    bestPostingTimes: ['2024-01-21T10:00:00Z', '2024-01-22T14:00:00Z', '2024-01-23T16:00:00Z'],
    audienceSize: 19800,
    growthRate: 7.2,
  },
  {
    platform: 'twitter',
    contentCount: 45,
    scheduledCount: 8,
    publishedCount: 37,
    engagementRate: 3.9,
    reachEstimate: 43000,
    priority: 'low',
    bestPostingTimes: ['2024-01-21T07:00:00Z', '2024-01-21T12:00:00Z', '2024-01-21T18:00:00Z'],
    audienceSize: 15600,
    growthRate: 5.1,
  },
  {
    platform: 'blog',
    contentCount: 16,
    scheduledCount: 2,
    publishedCount: 14,
    engagementRate: 7.6,
    reachEstimate: 34000,
    priority: 'high',
    bestPostingTimes: ['2024-01-22T09:00:00Z', '2024-01-23T10:00:00Z', '2024-01-24T11:00:00Z'],
    audienceSize: 12400,
    growthRate: 11.5,
  },
];

// Export all mock data
export default {
  mockTrends,
  mockAgentTasks,
  mockContentAssets,
  mockPipelineWeeks,
  mockDistributionPlatforms,
};

// Mock Approval Bundle for HITL checkpoint
export const mockApprovalBundle = {
  blogPost: {
    title: "AI Micro-interactions - The Future of UX in 2026",
    excerpt: "Discover how AI-powered micro-interactions are reshaping user experience design with predictive animations, smart feedback loops, and emotionally intelligent interfaces.",
    wordCount: 1247,
    readTime: 6,
  },
  infographic: {
    title: "AI Micro-interactions Visual Guide",
    description: "Data-driven breakdown of 7 key interaction patterns transforming UX in 2026",
    dimensions: "2400x1800px",
    format: "PNG/SVG",
  },
  reel: {
    title: "Watch AI Transform UX",
    description: "30-second educational talking avatar reel with precise lip-sync via SkyReels-V3",
    duration: "0:30",
  },
  scheduledTime: "Wednesday, Apr 16 at 10:00 AM EST",
};

// Mock Source Scan data -- 65+ sources monitored
export const mockSourceScan = [
  { "id": "src-001", "name": "Nielsen Norman Group", "status": "active" as const, "lastScan": "2 min ago", "articlesFound": 12, "iconColor": "#0066CC" },
  { "id": "src-002", "name": "UX Collective", "status": "active" as const, "lastScan": "5 min ago", "articlesFound": 28, "iconColor": "#FF6B6B" },
  { "id": "src-003", "name": "UX Planet", "status": "scanning" as const, "lastScan": "scanning...", "articlesFound": 15, "iconColor": "#4ECDC4" },
  { "id": "src-004", "name": "Dribbble Stories", "status": "active" as const, "lastScan": "8 min ago", "articlesFound": 34, "iconColor": "#EA4C89" },
  { "id": "src-005", "name": "Smashing Magazine", "status": "active" as const, "lastScan": "12 min ago", "articlesFound": 9, "iconColor": "#E85C33" },
  { "id": "src-006", "name": "A List Apart", "status": "active" as const, "lastScan": "15 min ago", "articlesFound": 6, "iconColor": "#3E5C76" },
  { "id": "src-007", "name": "CSS-Tricks", "status": "active" as const, "lastScan": "18 min ago", "articlesFound": 21, "iconColor": "#264DE4" },
  { "id": "src-008", "name": "UX Booth", "status": "pending" as const, "lastScan": "queued", "articlesFound": 0, "iconColor": "#6C757D" },
  { "id": "src-009", "name": "Awwwards", "status": "active" as const, "lastScan": "22 min ago", "articlesFound": 17, "iconColor": "#FFD700" },
  { "id": "src-010", "name": "Muzli Design", "status": "active" as const, "lastScan": "25 min ago", "articlesFound": 41, "iconColor": "#6366F1" },
  { "id": "src-011", "name": "InVision Blog", "status": "scanning" as const, "lastScan": "scanning...", "articlesFound": 8, "iconColor": "#FF3366" },
  { "id": "src-012", "name": "UX Design Weekly", "status": "active" as const, "lastScan": "30 min ago", "articlesFound": 13, "iconColor": "#28A745" },
];